/**
 * attachmentLimits — the one place the attachment size ceiling is written down
 * on the client.
 *
 * Three layers enforce this cap, and they are deliberately NOT equal:
 *
 *   1. this module (25 MB) — rejects the file in the browser, names the limit,
 *      and costs the user nothing but a click.
 *   2. de-hub `_ATTACHMENT_MAX_BYTES` (25 MB) — the real gate. The browser
 *      can't be trusted, and de-hub reads the whole upload into memory
 *      (`await file.read()`) across 4 gunicorn workers, so the ceiling is a
 *      memory guardrail, not a policy preference.
 *   3. nginx `client_max_body_size` (30m) — a backstop only, set ABOVE the
 *      product cap on purpose. nginx answers an oversized body with a bare
 *      413 and an HTML page, which reaches the user as "That file is too
 *      large to upload" with no size named and nothing in any server log.
 *      Keeping it higher means layers 1 and 2 — the ones that can explain
 *      themselves — always answer first.
 *
 * 25 MB clears the documents this app actually carries: scanned leases and
 * assignments run 1-5 MB, a title opinion with exhibits 10-20 MB. It also sits
 * above de-hub's separate 20 MB template-upload cap (`_FLEX_MAX_BYTES`), so
 * the two limits can't contradict each other.
 */

/** Hard ceiling for a single attachment, in bytes. */
export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

/** How the ceiling is spoken to users. Keep in step with the constant above. */
export const ATTACHMENT_MAX_LABEL = "25 MB";

/**
 * Format a byte count the way the attachments UI does, for error text.
 * Whole numbers stay whole ("30 MB"), fractions get one decimal ("28.4 MB").
 */
const megabytes = (bytes) => {
  const mb = bytes / (1024 * 1024);
  return `${mb >= 10 ? Math.round(mb) : Math.round(mb * 10) / 10} MB`;
};

/**
 * Why this file can't be uploaded, or `null` if it's fine.
 *
 * Returns a whole sentence naming both the file's size and the limit — "too
 * large" without either number leaves the user guessing how much to shrink it.
 *
 * @param {File|{name?: string, size?: number}} file
 * @returns {string|null}
 */
export const attachmentSizeError = (file) => {
  const size = file?.size;
  if (typeof size !== "number" || !Number.isFinite(size)) return null;
  if (size <= ATTACHMENT_MAX_BYTES) return null;
  const name = file?.name ? `"${file.name}" is` : "That file is";
  return (
    `${name} ${megabytes(size)} — larger than the ${ATTACHMENT_MAX_LABEL} ` +
    `limit for a single attachment. Split it or reduce the scan resolution ` +
    `and try again.`
  );
};

export default attachmentSizeError;
